/**
 * Czas pracy (spec 03 §3) — KPI miesiąca i realna ewidencja.
 *
 * Norma miesięczna liczy się na serwerze z dni roboczych i świąt PL, więc
 * kafel „z 168 h normy" pokazuje wartość dla konkretnego miesiąca, a nie stałą
 * z makiety — i to wymiar PRACOWNIKA, z jego etatu (fala 7b, #25). Makieta
 * kwitowała „Dodaj wpis" toastem — u nas wpis naprawdę trafia do akceptacji
 * przełożonego.
 *
 * Rodzaj umowy zmienia ekran (fala 7b, #35; D102): zleceniobiorca zapisuje
 * same godziny, bez kafli nadgodzin i pracy zdalnej, a przy dziele i B2B
 * ewidencja nie dotyczy — ekran mówi to wprost, zamiast pokazywać zera.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  ChevronLeft,
  ChevronRight,
  Clock,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Modal,
  Select,
  Spinner,
  StatCard,
  StatusPill,
  cx,
  useToast,
} from '@k1p4/ui'
import {
  EMPLOYEE_TIME_ENTRY_KINDS,
  FULL_TIME_FTE_BP,
  isShiftRange,
  MANDATE_TIME_ENTRY_KINDS,
  rangeHours,
  shiftMonth,
  workingTimeFraction,
} from '@k1p4/shared'
import { useFormat } from '../../format'
import { formatDate } from '@k1p4/i18n'
import { api, ApiError, type TimeEntryKind, apiDay, apiMonth } from '../../api/client'
import { pluralUnit } from './units'
import styles from './portal.module.css'

/**
 * Zdanie odmowy wpisu (fala 7b, grupy 39 i 133): kod z serwera albo nazwana
 * przyczyna walidacji, a „Spróbuj ponownie" dopiero wtedy, gdy przyczyny nie ma.
 */
export function timeEntryErrorKey(caught: unknown, exists: (key: string) => boolean): string {
  if (!(caught instanceof ApiError)) return 'portal.time.errorGeneric'
  switch (caught.code) {
    case 'PERIOD_CLOSED':
      return 'portal.time.errorClosed'
    case 'OUTSIDE_EMPLOYMENT':
      return 'portal.time.errorOutsideEmployment'
    case 'TIME_NOT_APPLICABLE':
      return 'portal.time.errorNotApplicable'
    case 'ABSENCE_DAY':
      return caught.details.absence === 'sick'
        ? 'portal.time.errorAbsenceSick'
        : 'portal.time.errorAbsenceLeave'
    case 'OVERLAPPING_ENTRY':
      return 'portal.time.errorOverlap'
    case 'DAY_HOURS_EXCEEDED':
      return 'portal.time.errorDayHours'
    default: {
      const issue = caught.issues.map((key) => `validation.timeEntry.${key}`).find(exists)
      return issue ?? 'portal.time.errorGeneric'
    }
  }
}

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

/** Klucz dnia tygodnia do `hrTime.weekdays` — ten sam słownik co kadry. */
function weekdayKey(date: string): (typeof WEEKDAY_KEYS)[number] {
  return WEEKDAY_KEYS[new Date(`${date}T00:00:00Z`).getUTCDay()]!
}

function isWeekendDay(date: string): boolean {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay()
  return day === 0 || day === 6
}

export function PortalTime() {
  const fmt = useFormat()
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = {
    date: useId(),
    hours: useId(),
    startsAt: useId(),
    endsAt: useId(),
    kind: useId(),
    note: useId(),
  }

  const [month, setMonth] = useState(() => apiMonth())
  const [formOpen, setFormOpen] = useState(false)
  /*
   * Ewidencja czasu pracy wymaga godziny rozpoczęcia i zakończenia pracy
   * (§ 6 pkt 1 lit. a rozporządzenia o dokumentacji pracowniczej), więc pola
   * „od–do" są tu pierwsze, a liczba godzin wynika z nich (Etap 39).
   */
  const [form, setForm] = useState({
    date: apiDay(),
    hours: '8',
    startsAt: '',
    endsAt: '',
    kind: 'praca' as TimeEntryKind,
    note: '',
  })
  const [error, setError] = useState<string | null>(null)
  /* Mój rozkład — wyłącznie przekazany przez pracodawcę (art. 129 § 3 k.p.). */
  const schedule = useQuery({
    queryKey: ['my-schedule', month],
    queryFn: () => api.mySchedule(month),
  })

  const time = useQuery({ queryKey: ['me', 'time', month], queryFn: () => api.meTime(month) })

  const submit = useMutation({
    mutationFn: () =>
      api.meAddTimeEntry({
        date: form.date,
        ...(form.startsAt && form.endsAt
          ? { startsAt: form.startsAt, endsAt: form.endsAt }
          : { hours: Number(form.hours.replace(',', '.')) }),
        kind: form.kind,
        ...(form.note.trim() ? { note: form.note.trim() } : {}),
      }),
    onSuccess: () => {
      toast.show(t(time.data?.approver === 'hr' ? 'portal.time.submittedHr' : 'portal.time.submitted'), {
        tone: 'success',
      })
      /* Wpis mógł dotyczyć innego miesiąca — pokazujemy ten, w którym wylądował. */
      setMonth(form.date.slice(0, 7))
      setFormOpen(false)
      setForm({ ...form, note: '' })
      void queryClient.invalidateQueries({ queryKey: ['me', 'time'] })
    },
    onError: (caught) => {
      setError(timeEntryErrorKey(caught, (key) => i18n.exists(key)))
    },
  })

  if (time.isPending) return <Spinner label={t('common.loading')} />
  if (time.isError) return <EmptyState title={t('common.error')} description={t('common.retry')} />

  const data = time.data
  if (!data.hasEmployeeRecord) {
    return <EmptyState icon={<Clock size={28} />} title={t('portal.noRecord')} />
  }
  if (data.regime === 'nie_dotyczy') {
    return (
      <EmptyState
        icon={<Clock size={28} />}
        title={t('portal.time.notApplicable')}
        description={t('portal.time.notApplicableBody')}
      />
    )
  }
  const mandate = data.regime === 'godziny_zlecenia'
  /* Bez przełożonego w aktach wpisy zatwierdzają kadry (fala 9b, jak grupa 111). */
  const byHr = data.approver === 'hr'
  const kinds = mandate ? MANDATE_TIME_ENTRY_KINDS : EMPLOYEE_TIME_ENTRY_KINDS
  /* Etat w nocie kafla tylko przy części etatu — „1/1" nic nie mówi. */
  const fteBp = data.fteBp ?? FULL_TIME_FTE_BP
  const fte = workingTimeFraction(fteBp / FULL_TIME_FTE_BP)
  const workedNote = mandate
    ? t('portal.time.kpiWorkedMandateNote')
    : fteBp === FULL_TIME_FTE_BP
      ? t('portal.time.kpiWorkedNote', { norm: data.employeeNormHours ?? data.normHours ?? 0 })
      : t('portal.time.kpiWorkedNotePart', { norm: data.employeeNormHours ?? 0, fte })

  const closed = data.periodStatus === 'closed'
  /*
   * Po ustaniu zatrudnienia ewidencja zostaje do wglądu, ale wpisu na dzień po
   * ostatnim dniu się nie dodaje (Fala 7, grupa 44). W miesiącu ustania wpis do
   * tego dnia jest w porządku, więc przycisk znika dopiero w miesiącach po nim.
   */
  const employment = data.employment
  const endDate = employment?.endDate ?? null
  const ended = employment?.ended === true
  const afterEmployment = ended && (endDate === null || `${month}-01` > endDate)
  /*
   * Godziny wynikają z zakresu (ADR-0040 pkt 1): przy podanym „od–do" pole
   * godzin pokazuje wyliczenie i jest tylko do odczytu — także przez północ,
   * bo zakres 22:00–06:00 to osiem godzin, a nie błąd (fala 7b, grupa 130).
   */
  const rangeGiven = form.startsAt !== '' && form.endsAt !== ''
  const rangeValid = rangeGiven && isShiftRange({ startsAt: form.startsAt, endsAt: form.endsAt })
  const derivedHours = rangeValid ? rangeHours({ startsAt: form.startsAt, endsAt: form.endsAt }) : null
  const hoursValid = rangeGiven ? rangeValid : Number(form.hours.replace(',', '.')) > 0

  return (
    <>
      <div className={styles.monthBar}>
        <Button
          size="sm"
          variant="secondary"
          aria-label={t('portal.time.previousMonth')}
          onClick={() => setMonth(shiftMonth(month, -1))}
        >
          <ChevronLeft size={16} />
        </Button>
        <span className={styles.monthLabel}>{fmt.month(month, 'long')}</span>
        <Button
          size="sm"
          variant="secondary"
          aria-label={t('portal.time.nextMonth')}
          onClick={() => setMonth(shiftMonth(month, 1))}
        >
          <ChevronRight size={16} />
        </Button>
      </div>

      {/*
        Miesiąc po ustaniu zatrudnienia nie ma normy ani rozkładu — kafle, rozkład
        i zachęta do pierwszego wpisu znikają, zostaje baner o ustaniu (fala 9b).
      */}
      {afterEmployment ? null : (
        <div className={styles.kpiGrid}>
          <StatCard
            label={t('portal.time.kpiWorked')}
            value={fmt.decimal(data.workedHours ?? 0)}
            unit={t('portal.time.unitHours')}
            note={workedNote}
          />
          {mandate ? null : (
            <>
              <StatCard
                label={t('portal.time.kpiOvertime')}
                value={fmt.decimal(data.overtimeHours ?? 0)}
                unit={t('portal.time.unitHours')}
                note={t('portal.time.kpiOvertimeNote')}
              />
              <StatCard
                label={t('portal.time.kpiRemote')}
                value={data.remoteDays ?? 0}
                unit={pluralUnit(i18n.language, data.remoteDays ?? 0, t, 'day')}
                note={t('portal.time.kpiRemoteNote')}
              />
            </>
          )}
        </div>
      )}

      {mandate ? (
        <InfoBanner tone="info" className={styles.banner}>
          {t('portal.time.mandateNote')}
        </InfoBanner>
      ) : null}

      {closed ? (
        <InfoBanner tone="warning" className={styles.banner}>
          {t('portal.time.periodClosed')}
        </InfoBanner>
      ) : null}
      {ended ? (
        <InfoBanner tone="info" className={styles.banner}>
          {endDate === null
            ? t('portal.time.employmentEndedNoDate')
            : t('portal.time.employmentEnded', { date: formatDate(endDate, 'dot') })}
        </InfoBanner>
      ) : null}

      {afterEmployment ? null : (
        <Card pad="md" className={styles.cta}>
          <div>
            <p className={styles.ctaTitle}>
              {t(mandate ? 'portal.time.ctaTitleMandate' : 'portal.time.ctaTitle')}
            </p>
            <p className={styles.ctaBody}>{t(byHr ? 'portal.time.ctaBodyHr' : 'portal.time.ctaBody')}</p>
          </div>
          <Button
            disabled={closed}
            onClick={() => {
              setError(null)
              setFormOpen(true)
            }}
          >
            {t('portal.time.ctaButton')}
          </Button>
        </Card>
      )}

      {/*
        Mój rozkład czasu pracy (Etap 41). Pracownik widzi go dopiero, gdy
        pracodawca go PRZEKAŻE — plan w robocie nie jest jeszcze rozkładem
        w rozumieniu art. 129 § 3 k.p.
      */}
      {afterEmployment ? null : (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('schedule.portalTitle')}</h2>
          {schedule.data === undefined ? null : schedule.data.applies === false ? (
            <p className={styles.rowNote}>{t('schedule.portalNotApplicable')}</p>
          ) : !schedule.data.published ? (
            <p className={styles.rowNote}>{t('schedule.portalNotPublished')}</p>
          ) : (
            <>
              <p className={styles.rowNote}>
                {(schedule.data.fteBp ?? FULL_TIME_FTE_BP) === FULL_TIME_FTE_BP
                  ? t('schedule.portalSummary', {
                      planned: schedule.data.plannedHours,
                      norm: schedule.data.employeeNormHours ?? schedule.data.normHours,
                      date: formatDate(schedule.data.publishedOn!, 'dot'),
                    })
                  : t('schedule.portalSummaryPart', {
                      planned: schedule.data.plannedHours,
                      norm: schedule.data.employeeNormHours,
                      fte: workingTimeFraction((schedule.data.fteBp ?? FULL_TIME_FTE_BP) / FULL_TIME_FTE_BP),
                      full: schedule.data.normHours,
                      date: formatDate(schedule.data.publishedOn!, 'dot'),
                    })}
              </p>
              <Card pad="flush" className={styles.list}>
                {schedule.data.days.map((day) => (
                  <div
                    key={day.date}
                    className={cx(styles.row, (isWeekendDay(day.date) || day.holiday === true) && styles.rowDayOff)}
                  >
                    {/* Dzień tygodnia i święto przy dacie, jak w karcie kadr (fala 7b, grupa 125). */}
                    <span className={styles.rowTitle}>
                      {t(`hrTime.weekdays.${weekdayKey(day.date)}`)} {formatDate(day.date, 'dot')}
                      {day.holiday === true ? (
                        <span className={styles.holidayMark}>{t('hrTime.holiday')}</span>
                      ) : null}
                    </span>
                    <span className={styles.rowMeta}>
                      {day.startsAt === null || day.endsAt === null
                        ? t('schedule.dayOff')
                        : `${day.startsAt}–${day.endsAt}`}
                    </span>
                  </div>
                ))}
              </Card>
            </>
          )}
        </section>
      )}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('portal.time.listTitle')}</h2>
        {data.entries.length === 0 ? (
          <EmptyState
            title={t('portal.time.empty')}
            description={t(
              afterEmployment
                ? 'portal.time.emptyAfterEmployment'
                : byHr
                  ? 'portal.time.emptyBodyHr'
                  : 'portal.time.emptyBody',
            )}
          />
        ) : (
          <Card pad="flush" className={styles.list}>
            {data.entries.map((entry) => (
              <div key={entry.id} className={styles.row}>
                <span className={styles.rowIcon} aria-hidden="true">
                  <Clock size={18} />
                </span>
                <div className={styles.rowBody}>
                  <p className={styles.rowTitle}>
                    {formatDate(entry.date, 'dot')}
                    {entry.startsAt && entry.endsAt
                      ? ` · ${entry.startsAt.slice(0, 5)}–${entry.endsAt.slice(0, 5)}`
                      : ''}{' '}
                    · {fmt.hours(entry.hours)} ·{' '}
                    {t(`portal.time.kinds.${entry.kind}`)}
                  </p>
                  {entry.note ? <p className={styles.rowNote}>{entry.note}</p> : null}
                </div>
                <StatusPill tone={entry.approved ? 'success' : 'warning'}>
                  {t(entry.approved ? 'portal.time.approved' : 'portal.time.awaiting')}
                </StatusPill>
              </div>
            ))}
          </Card>
        )}
      </section>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t('portal.time.formTitle')}
        footer={
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setFormOpen(false)}>
              {t('portal.time.cancel')}
            </Button>
            <Button disabled={!hoursValid || submit.isPending} onClick={() => submit.mutate()}>
              {submit.isPending ? (
                <Spinner label={t('portal.time.submitting')} />
              ) : (
                t('portal.time.submit')
              )}
            </Button>
          </div>
        }
      >
        {error !== null ? <InfoBanner tone="danger">{t(error)}</InfoBanner> : null}
        <div className={styles.formGrid}>
          <FormField label={t('portal.time.date')} htmlFor={ids.date}>
            <Input
              type="date"
              value={form.date}
              onChange={(event) => setForm({ ...form, date: event.target.value })}
            />
          </FormField>
          {/*
            Godziny „od–do" są wymogiem ewidencji; liczba godzin wylicza się
            z nich, więc pole niżej zostaje dla wpisów bez zakresu.
          */}
          <FormField label={t('portal.time.startsAt')} htmlFor={ids.startsAt}>
            <Input
              id={ids.startsAt}
              type="time"
              value={form.startsAt}
              onChange={(event) => setForm({ ...form, startsAt: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('portal.time.endsAt')}
            htmlFor={ids.endsAt}
            hint={t('portal.time.rangeHint')}
            /*
             * Odwrócony albo za długi zakres mówi, co poprawić, zamiast samego
             * wyszarzonego przycisku (fala 9b, grupa 39).
             */
            {...(rangeGiven && !rangeValid ? { error: t('validation.timeEntry.range') } : {})}
          >
            <Input
              id={ids.endsAt}
              type="time"
              value={form.endsAt}
              onChange={(event) => setForm({ ...form, endsAt: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('portal.time.hours')}
            htmlFor={ids.hours}
            {...(derivedHours === null ? {} : { hint: t('portal.time.hoursFromRange') })}
          >
            <Input
              id={ids.hours}
              inputMode="decimal"
              readOnly={rangeGiven}
              /* Przy błędnym zakresie pole nie udaje „8 h” — godzin z niego nie ma. */
              value={rangeGiven ? (derivedHours === null ? '' : fmt.decimal(derivedHours)) : form.hours}
              onChange={(event) => setForm({ ...form, hours: event.target.value })}
            />
          </FormField>
          <FormField className={styles.formFull} label={t('portal.time.kind')} htmlFor={ids.kind}>
            <Select
              value={form.kind}
              onChange={(event) => setForm({ ...form, kind: event.target.value as TimeEntryKind })}
            >
              {kinds.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`portal.time.kinds.${kind}`)}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField className={styles.formFull} label={t('portal.time.note')} htmlFor={ids.note}>
            <textarea
              id={ids.note}
              className={styles.textarea}
              rows={3}
              placeholder={t('portal.time.notePlaceholder')}
              value={form.note}
              onChange={(event) => setForm({ ...form, note: event.target.value })}
            />
          </FormField>
        </div>
      </Modal>
    </>
  )
}
